import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { TenantProvider } from './contexts/TenantContext';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

import DailySchedulePage from './screens/private/DailySchedulePage';
import EmployeeManagementPage from './screens/private/EmployeeManagementPage';
import Login from './screens/Login';
import AdminDashboard from './screens/private/AdminDashboard';
import PrivateRoute from './components/PrivateRoute';
import AvailabilityPage from './screens/private/AvailabilityPage';
import BookingPage from './screens/BookingPage';
import CancelAppointment from './screens/CancelAppointment';
import AppointmentTypesPage from './screens/private/AppointmentTypesPage';
import HeaderBar from './components/headerbar';
import CompanySettings from './components/admin/CompanySettings';
import Footer from './components/Footer'; // Import der Footer-Komponente

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      setIsAuthenticated(false);
    });
  };

  const theme = createTheme({
    palette: {
      background: {
        paper: '#f8f9fa',
      },
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <TenantProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          {isAuthenticated && <HeaderBar onLogout={handleLogout} />}
          <Routes>
            {/* Öffentliche Routen */}
            <Route
              path="/login"
              element={
                isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <Login />
              }
            />
            <Route path="/" element={<BookingPage />} />
            <Route path="/cancel" element={<CancelAppointment />} />

            {/* Geschützte Admin-Routen */}
            <Route
              path="/admin/dashboard"
              element={
                isAuthenticated ? (
                  <PrivateRoute roles={['admin']}>
                    <AdminDashboard />
                  </PrivateRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/manage-availability"
              element={
                isAuthenticated ? (
                  <PrivateRoute roles={['admin']}>
                    <AvailabilityPage />
                  </PrivateRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/appointment-types"
              element={
                isAuthenticated ? (
                  <PrivateRoute roles={['admin']}>
                    <AppointmentTypesPage />
                  </PrivateRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/user-management"
              element={
                isAuthenticated ? (
                  <PrivateRoute roles={['admin']}>
                    <EmployeeManagementPage />
                  </PrivateRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/company-settings"
              element={
                isAuthenticated ? (
                  <PrivateRoute roles={['admin']}>
                    <CompanySettings />
                  </PrivateRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            {/* Tagesplan Route */}
            <Route
              path="/schedule"
              element={
                isAuthenticated ? (
                  <PrivateRoute roles={['admin']}>
                    <DailySchedulePage />
                  </PrivateRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Standard-Redirect */}
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
          <Footer /> {/* Footer-Komponente */}
        </BrowserRouter>
      </ThemeProvider>
    </TenantProvider>
  );
}

export default App;
